import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { cloneDeep } from "lodash-es";
import { onScopeDispose } from "vue";
import { useRoute } from "vue-router";


export function useSimulationAPI() {
  let parentOrigin: string | null = null;

  const simulationStore = useSimulationStore();
  const simulationViewStore = useSimulationViewStore();
  const route = useRoute();

  // =========================
  // RECEIVING MESSAGES FROM PARENT

  async function handleAction(message: { store: string; action?: string; payload?: any }) {
    const storeName = message.store;
    const action = message.action;
    const payload = message.payload;

    if (!storeName || !action) {
      console.warn(`Received message with missing store or action: ${JSON.stringify(message)}`);
      return;
    }
    if (typeof storeName !== "string" || typeof action !== "string") {
      console.warn(`Received message with invalid store or action type: ${JSON.stringify(message)}`);
      return;
    }

    if (storeName === "simulation") {
      if (!(action in simulationStore)) {
        console.warn(`Received message for unknown simulation action: ${action}`);
        return;
      }
      console.log("incoming action", "simulation", action, payload);
      await simulationStore[action](...payload, { receivedFromParent: true });
    } else if (storeName === "simulationView") {
      console.log("incoming action", "simulationView", action, payload);
      switch (action) {
        case "updateImpulses":
          await simulationViewStore.updateImpulses(...payload, { receivedFromParent: true });
          break;
        case "saveSimulation":
          await simulationViewStore.saveSimulation(...payload, { receivedFromParent: true });
          break;
        case "visualizeRound":
          if (route.meta.areaName === "simulation") {
            await simulationViewStore.visualizeRound(...payload, { receivedFromParent: true });
          } else if (simulationViewStore.simulator) {
            simulationViewStore.goToRound(simulationViewStore.simulator.currentRoundIndex, { receivedFromParent: true });
          }
          break;
        default:
          console.warn(`Received unknown simulationView message action: ${action}`);
      }
    } else {
      console.warn(`Received message for unknown store: ${storeName}`);
    }
  }

  function getState(message: { store: string; key: string }) {
    if (!parentOrigin) {
      console.warn(`Received state request message before parent origin was established: ${JSON.stringify(message)}`);
      return;
    }

    const { store, key } = message;
    if (store === "simulationView") {
      if ( key === "simulation") {
        sendActiveSimulationState();
        return;
      }
    }

    console.warn("Not handling state request", message);
  }

  function setState(message: { store: string; key: string; state: any }) {
    const { store, key, state } = message;
    console.log("incoming state update", store, key, state);
    if (store === "simulationView") {
      if (key === "simulation") {
        const simulation = state;
        simulationStore.items[simulation.id] = simulation;
        simulationViewStore.loadSimulation({ simulationId: simulation.id, roundIndex: simulation.currentRoundIndex });
        return;
      }
    }

    console.warn("Not handling state update", message);
  }

  function onMessage(event: MessageEvent) {
    const message = event.data;

    if (parentOrigin == null) {
      // Only react to messages once the parent origin has been established
      if (message.type === "init") {
        parentOrigin = event.origin;
        console.log(`Initialized simulation API with parent origin: ${parentOrigin}`);
      }
      return;
    }

    if (event.origin !== parentOrigin) {
      console.warn(`Received message from unauthorized origin: ${event.origin}`);
      return;
    }
    
    const type: string = (typeof message === "object" && "type" in message && typeof message.type === "string") ? message.type : "unknown";
    if (type === "action") {
      handleAction(message);
    } else if (type === "get") {
      getState(message);
    } else if (type === "set") {
      setState(message);
    } else {
      console.warn(`Received invalid message: ${message}`);
    }
  }

  function bindMessageListener() {
    console.log("simulationAPI: Binding message listener");
    window.addEventListener("message", onMessage);
  }
  function unbindMessageListener() {
    console.log("simulationAPI: Unbinding message listener");
    window.removeEventListener("message", onMessage);
  }

  bindMessageListener();
  onScopeDispose(() => {
    unbindMessageListener();
  });

  // ==========================
  // SENDING MESSAGES TO PARENT

  let simActionsDepth = 0;

  function sendActiveSimulationState() {
    if (!parentOrigin) return;

    const simulation = cloneDeep(simulationViewStore.simulation);

    parent.postMessage({
      type: "snapshot",
      store: "simulationView",
      key: "simulation",
      state: simulation,
    }, parentOrigin);
  }

  const unsubscribeSimulation = simulationStore.$onAction(({ name, args, after }) => {
    if (!parentOrigin) return;

    // Do not post messages for actions that were received from the parent to avoid infinite loops
    if (args && args.length > 0) {
      const lastArg = args[args.length - 1];
      if (lastArg && typeof lastArg === "object" && "receivedFromParent" in lastArg && lastArg.receivedFromParent) {
        return;
      }
    }

    if (simActionsDepth > 0) {
      console.log("Not sending message for nested simulation action", name);
      return;
    }

    simActionsDepth++;

    console.debug("send message", "simulation", name);
    parent.postMessage({
      type: "action",
      store: "simulation",
      action: name,
      payload: args,
    }, parentOrigin);

    after(() => {
      simActionsDepth--;
    });
  });

  const unsubscribeSimulationView = simulationViewStore.$onAction(({ name, args }) => {
    if (!parentOrigin) return;
    type Action = typeof name;

    // Do not post messages for actions that were received from the parent to avoid infinite loops
    if (args && args.length > 0) {
      const lastArg = args[args.length - 1];
      if (lastArg && typeof lastArg === "object" && "receivedFromParent" in lastArg && lastArg.receivedFromParent) {
        return;
      }
    }

    const preventedActions: Action[] = [
      "openSimulationQuickInfo",
      "closeSimulationQuickInfo",
      "updateSimulationQuickInfo",
      "setRoundDrawerOpen",
      "toggleSimulationInfo",
      "loadSimulation"
    ];

    if (preventedActions.includes(name)) {
      // do not post messages for these actions
      return;
    }

    console.debug("send message", "simulationView", name);
    parent.postMessage({
      type: "action",
      store: "simulationView",
      action: name,
      payload: args,
    }, parentOrigin);
  });

  function dispose() {
    unsubscribeSimulation();
    unsubscribeSimulationView();
  }

  parent.postMessage({
    type: "simulationApiReady",
  }, parentOrigin || "*");

  onScopeDispose(() => {
    dispose();
    console.log("Disposed simulation API listeners");
  });
}
